/**
 * Utility for managing Sanctum API tokens in-memory.
 * 
 * We store the token in a private variable within this module's scope
 * to protect it from being easily extracted by XSS scripts via localStorage.
 */

let inMemoryToken: string | null = null;

/** In-flight promise to deduplicate concurrent ensureToken() calls. */
let tokenFetchPromise: Promise<string | null> | null = null;

/**
 * Get the current in-memory token.
 */
export function getStoredToken(): string | null {
  return inMemoryToken;
}

/**
 * Store the Sanctum token in-memory.
 */
export function setStoredToken(token: string): void {
  inMemoryToken = token;
}

/**
 * Clear the token.
 */
export function clearStoredToken(): void {
  inMemoryToken = null;
}

/**
 * Fetch a new Sanctum token from the server using the HttpOnly session.
 * 
 * Since the session cookie is HttpOnly, XSS cannot steal it.
 * This method is the secure bridge between the session and the performant Bearer token.
 * 
 * Deduplicates concurrent calls: multiple callers share a single in-flight fetch.
 */
export async function ensureToken(): Promise<string | null> {
  // If we already have it in memory, return it
  if (inMemoryToken) return inMemoryToken;

  // If a fetch is already in progress, wait for it instead of starting another
  if (tokenFetchPromise) return tokenFetchPromise;

  tokenFetchPromise = (async () => {
    try {
      const response = await fetch("/auth/token", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          ...(document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
            ? { "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') as string }
            : {})
        },
        credentials: "same-origin" // Relies on the HttpOnly session cookie
      });

      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setStoredToken(data.token);
          return data.token;
        }
      }
      return null;
    } catch (error) {
      console.warn("[Auth] Failed to automatically retrieve Sanctum token:", error);
      return null;
    } finally {
      tokenFetchPromise = null;
    }
  })();

  return tokenFetchPromise;
}
